import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';
import { lookupUsers } from '../api';
import type { UserListItem } from '../api';
import { ArrowLeft, Plus, X, Loader2, Lock, Search, UserPlus, Mail, Pencil } from 'lucide-react';

interface Theme {
  name: string;
  description: string;
}

interface Criterion {
  name: string;
  weight: number;
  maxScore: number;
}

interface EventDetail {
  eventId: string;
  name: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  submissionDeadline: string;
  themes: Theme[];
  scoringCriteria: Criterion[];
  settings: { minTeamSize: number; maxTeamSize: number; timerDurationSeconds: number; allowSelfJoin?: boolean; enableMentors?: boolean };
  isTest?: boolean;
  coverImageUrl?: string;
}

function toLocalDatetime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventEditPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventStatus, setEventStatus] = useState<string>('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submissionDeadline, setSubmissionDeadline] = useState('');
  const [themes, setThemes] = useState<Theme[]>([{ name: '', description: '' }]);
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [minTeamSize, setMinTeamSize] = useState(2);
  const [maxTeamSize, setMaxTeamSize] = useState(5);
  const [timerDuration, setTimerDuration] = useState(300);
  const [allowSelfJoin, setAllowSelfJoin] = useState(true);
  const [enableMentors, setEnableMentors] = useState(true);
  const [isTest, setIsTest] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [feedbackIntro, setFeedbackIntro] = useState('');
  const [feedbackQuestions, setFeedbackQuestions] = useState<Array<{ id: string; type: 'text' | 'rating'; label: string; maxRating?: number }>>([]);
  const [judges, setJudges] = useState<string[]>([]);
  const [judgeDetails, setJudgeDetails] = useState<Record<string, UserListItem>>({});
  const [judgeSearch, setJudgeSearch] = useState('');
  const [judgeResults, setJudgeResults] = useState<Array<{ sub: string; name: string; email: string }>>([]);
  const [judgeSearching, setJudgeSearching] = useState(false);

  const originalRef = useRef<Record<string, unknown>>({});

  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
  const isPublished = eventStatus !== 'draft'; // themes/criteria locked once published

  // Hydrate judge display details (name, email) whenever the list of yumIds changes.
  useEffect(() => {
    const missing = judges.filter((id) => !judgeDetails[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    lookupUsers(missing)
      .then((users) => {
        if (cancelled) return;
        setJudgeDetails((prev) => {
          const next = { ...prev };
          for (const u of users) next[u.yumId] = u;
          return next;
        });
      })
      .catch(() => {
        // Non-blocking: list will fall back to yumId display
      });
    return () => {
      cancelled = true;
    };
  }, [judges, judgeDetails]);

  useEffect(() => {
    networkManager
      .get<{ status: boolean; data: EventDetail }>(`/api/events/${eventId}`)
      .then((res) => {
        const ev = res.data;

        if (['completed', 'archived'].includes(ev.status)) {
          navigate(`/events/${eventId}`, {
            state: { message: 'Event cannot be edited' },
            replace: true,
          });
          return;
        }

        setEventStatus(ev.status);
        setName(ev.name);
        setDescription(ev.description ?? '');
        setStartDate(toLocalDatetime(ev.startDate));
        setEndDate(toLocalDatetime(ev.endDate));
        setSubmissionDeadline(toLocalDatetime(ev.submissionDeadline));
        setThemes(ev.themes.length > 0 ? ev.themes : [{ name: '', description: '' }]);
        setCriteria(
          ev.scoringCriteria.length > 0
            ? ev.scoringCriteria
            : [
                { name: 'Innovation', weight: 30, maxScore: 10 },
                { name: 'Feasibility', weight: 25, maxScore: 10 },
                { name: 'Impact', weight: 25, maxScore: 10 },
                { name: 'Presentation', weight: 20, maxScore: 10 },
              ],
        );
        setMinTeamSize(ev.settings?.minTeamSize ?? 2);
        setMaxTeamSize(ev.settings?.maxTeamSize ?? 5);
        setTimerDuration(ev.settings?.timerDurationSeconds ?? 300);
        setAllowSelfJoin(ev.settings?.allowSelfJoin ?? true);
        setEnableMentors(ev.settings?.enableMentors ?? true);
        setIsTest(ev.isTest ?? false);
        setCoverImageUrl(ev.coverImageUrl ?? '');
        const ff = (ev as unknown as Record<string, unknown>).feedbackForm as { enabled?: boolean; introHtml?: string; questions?: Array<{ id: string; type: 'text' | 'rating'; label: string; maxRating?: number }> } | undefined;
        setFeedbackEnabled(ff?.enabled ?? false);
        setFeedbackIntro(ff?.introHtml ?? '');
        setFeedbackQuestions(ff?.questions ?? []);
        setJudges((ev as unknown as Record<string, unknown>).judges as string[] ?? []);

        originalRef.current = {
          name: ev.name,
          description: ev.description ?? '',
          startDate: ev.startDate,
          endDate: ev.endDate,
          submissionDeadline: ev.submissionDeadline,
          themes: ev.themes,
          scoringCriteria: ev.scoringCriteria,
          minTeamSize: ev.settings?.minTeamSize ?? 2,
          maxTeamSize: ev.settings?.maxTeamSize ?? 5,
          timerDuration: ev.settings?.timerDurationSeconds ?? 300,
          allowSelfJoin: ev.settings?.allowSelfJoin ?? true,
          enableMentors: ev.settings?.enableMentors ?? true,
          isTest: ev.isTest ?? false,
          coverImageUrl: ev.coverImageUrl ?? '',
          feedbackForm: ff ?? { enabled: false, introHtml: '', questions: [] },
          judges: (ev as unknown as Record<string, unknown>).judges as string[] ?? [],
        };
      })
      .catch(() => {
        setError('Failed to load event');
      })
      .finally(() => setLoading(false));
  }, [eventId, navigate]);

  function buildChangedFields(): Record<string, unknown> {
    const changes: Record<string, unknown> = {};
    const orig = originalRef.current;

    if (name !== orig.name) changes.name = name;
    if (description !== orig.description) changes.description = description;

    const newStart = new Date(startDate).toISOString();
    if (newStart !== orig.startDate) changes.startDate = newStart;

    const newEnd = new Date(endDate).toISOString();
    if (newEnd !== orig.endDate) changes.endDate = newEnd;

    const newDeadline = new Date(submissionDeadline).toISOString();
    if (newDeadline !== orig.submissionDeadline) changes.submissionDeadline = newDeadline;

    if (!isPublished) {
      const filteredThemes = themes.filter((t) => t.name.trim());
      if (JSON.stringify(filteredThemes) !== JSON.stringify(orig.themes)) {
        changes.themes = filteredThemes;
      }

      const filteredCriteria = criteria.filter((c) => c.name.trim());
      if (JSON.stringify(filteredCriteria) !== JSON.stringify(orig.scoringCriteria)) {
        changes.scoringCriteria = filteredCriteria;
      }
    }

    const settingsChanged =
      minTeamSize !== orig.minTeamSize ||
      maxTeamSize !== orig.maxTeamSize ||
      timerDuration !== orig.timerDuration ||
      allowSelfJoin !== orig.allowSelfJoin ||
      enableMentors !== orig.enableMentors;

    if (settingsChanged) {
      changes.settings = {
        minTeamSize,
        maxTeamSize,
        timerDurationSeconds: timerDuration,
        allowSelfJoin,
        enableMentors,
      };
    }

    // Always include feedbackForm when it's enabled or was previously enabled
    // (JSON key ordering from DynamoDB can differ, making string comparison unreliable)
    const currentFeedbackForm = { enabled: feedbackEnabled, introHtml: feedbackIntro, questions: feedbackQuestions };
    if (feedbackEnabled || (orig.feedbackForm as { enabled?: boolean })?.enabled) {
      changes.feedbackForm = currentFeedbackForm;
    }

    if (JSON.stringify(judges) !== JSON.stringify(orig.judges)) {
      changes.judges = judges;
    }

    if (isTest !== orig.isTest) {
      changes.isTest = isTest;
    }

    if (coverImageUrl !== orig.coverImageUrl) {
      changes.coverImageUrl = coverImageUrl;
    }

    return changes;
  }

  function validate(): string | null {
    if (name.trim().length < 3) return 'Event name must be at least 3 characters';
    if (name.trim().length > 100) return 'Event name must be 100 characters or fewer';

    if (!startDate || !endDate || !submissionDeadline) return 'All dates are required';

    const start = new Date(startDate);
    const deadline = new Date(submissionDeadline);
    const end = new Date(endDate);

    if (start >= deadline) return 'Start date must be before submission deadline';
    if (deadline >= end) return 'Submission deadline must be before end date';

    if (!isPublished) {
      const validThemes = themes.filter((t) => t.name.trim());
      if (validThemes.length === 0) return 'At least one theme is required';

      const validCriteria = criteria.filter((c) => c.name.trim());
      if (validCriteria.length === 0) return 'At least one scoring criterion is required';
      if (totalWeight !== 100) return 'Scoring criteria weights must sum to 100';
    }

    if (minTeamSize > maxTeamSize) return 'Min team size must be ≤ max team size';

    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const changes = buildChangedFields();
      if (Object.keys(changes).length === 0) {
        navigate(`/events/${eventId}`);
        return;
      }

      await networkManager.put<{ status: boolean }>(`/api/events/${eventId}`, changes);
      navigate(`/events/${eventId}`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save event';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate(`/events/${eventId}`)} className="btn-ghost mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Event
      </button>

      <h1 className="font-display text-3xl font-bold text-ink">Edit Event</h1>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-8">
        {/* Basics */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Basics</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-ink">Name *</label>
              <input
                type="text"
                className="input-base mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="VN Hackathon 2026"
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Description</label>
              <textarea
                className="input-base mt-1"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Build the future of Byte"
                maxLength={2000}
              />
            </div>

            {/* Cover Image */}
            <div>
              <label className="text-sm font-medium text-ink">Cover Image</label>
              <p className="text-xs text-ink-tertiary">Banner shown at the top of the event page and on the dashboard (PNG, JPG, WebP, max 5 MB, recommended 1600×400)</p>
              {coverImageUrl ? (
                <div className="mt-2">
                  <div className="relative overflow-hidden rounded-lg border border-ring aspect-[4/1] bg-gray-100">
                    <img src={coverImageUrl} alt="Event cover" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setCoverImageUrl('')}
                      className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
                      title="Remove cover image"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-accent hover:underline">
                    {coverUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pencil className="h-3 w-3" />}
                    Replace image
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      disabled={coverUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !eventId) return;
                        if (file.size > 5 * 1024 * 1024) { setCoverError('Image must be under 5 MB'); return; }
                        setCoverUploading(true);
                        setCoverError(null);
                        try {
                          const presign = await networkManager.post<{ status: boolean; data: { uploadUrl: string; imageUrl: string } }>(`/api/events/${eventId}/cover-image/upload`, { fileName: file.name, contentType: file.type });
                          await fetch(presign.data.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
                          setCoverImageUrl(presign.data.imageUrl);
                        } catch {
                          setCoverError('Upload failed. Please try again.');
                        } finally {
                          setCoverUploading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
              ) : (
                <label className="mt-2 flex aspect-[4/1] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-ring bg-gray-50 text-ink-tertiary transition-colors hover:border-accent hover:bg-gray-100">
                  {coverUploading ? (
                    <><Loader2 className="h-6 w-6 animate-spin" /><span className="mt-2 text-sm">Uploading...</span></>
                  ) : (
                    <><Plus className="h-6 w-6" /><span className="mt-2 text-sm font-medium">Click to upload cover image</span></>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    disabled={coverUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !eventId) return;
                      if (file.size > 5 * 1024 * 1024) { setCoverError('Image must be under 5 MB'); return; }
                      setCoverUploading(true);
                      setCoverError(null);
                      try {
                        const presign = await networkManager.post<{ status: boolean; data: { uploadUrl: string; imageUrl: string } }>(`/api/events/${eventId}/cover-image/upload`, { fileName: file.name, contentType: file.type });
                        await fetch(presign.data.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
                        setCoverImageUrl(presign.data.imageUrl);
                      } catch {
                        setCoverError('Upload failed. Please try again.');
                      } finally {
                        setCoverUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              )}
              {coverError && <p className="mt-2 text-xs text-red-600">{coverError}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-ink">Start Date *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink">Submission Deadline *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={submissionDeadline}
                  onChange={(e) => setSubmissionDeadline(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink">End Date *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {/* Themes */}
        <section className="card p-5">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-ink">Themes</h2>
            {isPublished && <Lock className="h-4 w-4 text-ink-tertiary" />}
          </div>
          {isPublished && (
            <p className="mt-1 text-sm text-amber-600">
              Cannot modify themes after event is published
            </p>
          )}
          <div className="mt-4 space-y-3">
            {themes.map((theme, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="input-base"
                    value={theme.name}
                    onChange={(e) => {
                      const t = [...themes];
                      t[i] = { ...t[i], name: e.target.value };
                      setThemes(t);
                    }}
                    placeholder="Theme name"
                    disabled={isPublished}
                  />
                  <input
                    type="text"
                    className="input-base"
                    value={theme.description}
                    onChange={(e) => {
                      const t = [...themes];
                      t[i] = { ...t[i], description: e.target.value };
                      setThemes(t);
                    }}
                    placeholder="Theme description"
                    disabled={isPublished}
                  />
                </div>
                {!isPublished && themes.length > 1 && (
                  <button
                    type="button"
                    className="btn-ghost self-start text-ink-tertiary"
                    onClick={() => setThemes(themes.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!isPublished && (
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setThemes([...themes, { name: '', description: '' }])}
              >
                <Plus className="h-4 w-4" /> Add Theme
              </button>
            )}
          </div>
        </section>

        {/* Scoring Criteria */}
        <section className="card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-ink">Scoring Criteria</h2>
              {isPublished && <Lock className="h-4 w-4 text-ink-tertiary" />}
            </div>
            <span
              className={`text-sm font-medium ${totalWeight === 100 ? 'text-green-600' : 'text-red-600'}`}
            >
              Total: {totalWeight}%
            </span>
          </div>
          {isPublished && (
            <p className="mt-1 text-sm text-amber-600">
              Cannot modify scoring criteria after event is published
            </p>
          )}
          <div className="mt-4 space-y-3">
            {criteria.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <input
                  type="text"
                  className="input-base flex-1"
                  value={c.name}
                  onChange={(e) => {
                    const cr = [...criteria];
                    cr[i] = { ...cr[i], name: e.target.value };
                    setCriteria(cr);
                  }}
                  placeholder="Criterion name"
                  disabled={isPublished}
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className="input-base w-20 text-center"
                    value={c.weight}
                    onChange={(e) => {
                      const cr = [...criteria];
                      cr[i] = { ...cr[i], weight: parseInt(e.target.value) || 0 };
                      setCriteria(cr);
                    }}
                    min={1}
                    max={100}
                    disabled={isPublished}
                  />
                  <span className="text-sm text-ink-secondary">%</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-ink-secondary">Max</span>
                  <input
                    type="number"
                    className="input-base w-16 text-center"
                    value={c.maxScore}
                    onChange={(e) => {
                      const cr = [...criteria];
                      cr[i] = { ...cr[i], maxScore: parseInt(e.target.value) || 10 };
                      setCriteria(cr);
                    }}
                    min={1}
                    max={100}
                    disabled={isPublished}
                  />
                </div>
                {!isPublished && criteria.length > 1 && (
                  <button
                    type="button"
                    className="btn-ghost text-ink-tertiary"
                    onClick={() => setCriteria(criteria.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!isPublished && (
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setCriteria([...criteria, { name: '', weight: 0, maxScore: 10 }])}
              >
                <Plus className="h-4 w-4" /> Add Criterion
              </button>
            )}
          </div>
        </section>

        {/* Settings */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Settings</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-sm font-medium text-ink">Min Team Size</label>
              <input
                type="number"
                className="input-base mt-1"
                value={minTeamSize}
                onChange={(e) => setMinTeamSize(parseInt(e.target.value) || 2)}
                min={1}
                max={20}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Max Team Size</label>
              <input
                type="number"
                className="input-base mt-1"
                value={maxTeamSize}
                onChange={(e) => setMaxTeamSize(parseInt(e.target.value) || 5)}
                min={1}
                max={50}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Timer (seconds per team)</label>
              <input
                type="number"
                className="input-base mt-1"
                value={timerDuration}
                onChange={(e) => setTimerDuration(parseInt(e.target.value) || 300)}
                min={60}
                max={1800}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-ring p-3">
            <div>
              <label className="text-sm font-medium text-ink">Allow Self-Join</label>
              <p className="text-xs text-ink-tertiary">When off, only admins/organizers can add users to teams</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allowSelfJoin}
              onClick={() => setAllowSelfJoin(!allowSelfJoin)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${allowSelfJoin ? 'bg-accent' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${allowSelfJoin ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-ring p-3">
            <div>
              <label className="text-sm font-medium text-ink">Enable Mentors</label>
              <p className="text-xs text-ink-tertiary">When off, mentor feedback features are hidden for this event</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enableMentors}
              onClick={() => setEnableMentors(!enableMentors)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${enableMentors ? 'bg-accent' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enableMentors ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className={`mt-4 flex items-center justify-between rounded-lg border p-3 ${isTest ? 'border-purple-300 bg-purple-50' : 'border-ring'}`}>
            <div>
              <label className="text-sm font-medium text-ink">Mark as Test Event</label>
              <p className="text-xs text-ink-tertiary">Test events are hidden by default — only visible to admins who enable "Show test events"</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isTest}
              onClick={() => setIsTest(!isTest)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${isTest ? 'bg-purple-500' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${isTest ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </section>

        {/* Event Feedback Form Config */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Event Feedback Survey</h2>
          <p className="mt-1 text-sm text-ink-tertiary">Configure a feedback form that participants can fill out once per event.</p>

          <div className="mt-4 flex items-center justify-between rounded-lg border border-ring p-3">
            <div>
              <label className="text-sm font-medium text-ink">Enable Feedback Form</label>
              <p className="text-xs text-ink-tertiary">When on, participants see a feedback form on the event page</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={feedbackEnabled}
              onClick={() => setFeedbackEnabled(!feedbackEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${feedbackEnabled ? 'bg-accent' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${feedbackEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {feedbackEnabled && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-ink">Intro Text (HTML supported)</label>
                <textarea
                  className="input-base mt-1 min-h-[120px] resize-y font-mono text-xs"
                  value={feedbackIntro}
                  onChange={(e) => setFeedbackIntro(e.target.value)}
                  placeholder="<h2>Thank you for participating!</h2><p>Your feedback helps us improve future hackathons.</p>"
                  rows={Math.max(5, feedbackIntro.split('\n').length + 1)}
                />
                {feedbackIntro.trim() && (
                  <div className="mt-2">
                    <label className="text-xs font-medium text-ink-tertiary">Preview</label>
                    <div
                      className="mt-1 rounded-lg border border-ring bg-surface-sunken p-3 html-content max-w-none text-sm text-ink"
                      dangerouslySetInnerHTML={{ __html: feedbackIntro }}
                    />
                    <style>{`.html-content * { margin: 0; padding: 0; } .html-content h1 { font-size: 1.125rem; font-weight: 700; margin-bottom: 0.25rem; } .html-content h2 { font-size: 1rem; font-weight: 600; margin-top: 0.75rem; margin-bottom: 0.25rem; } .html-content h3 { font-size: 0.875rem; font-weight: 600; margin-top: 0.5rem; } .html-content p { margin-bottom: 0.25rem; line-height: 1.4; } .html-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; } .html-content ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25rem 0; } .html-content li { line-height: 1.4; } .html-content .highlight { font-weight: 600; color: #7c3aed; } .html-content strong { font-weight: 600; } .html-content a { color: #7c3aed; text-decoration: underline; } .html-content div { margin-bottom: 0.25rem; }`}</style>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-ink">Questions</label>
                <div className="mt-2 space-y-3">
                  {feedbackQuestions.map((q, idx) => (
                    <div key={q.id} className="rounded-lg border border-ring p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            className="input-base"
                            value={q.label}
                            onChange={(e) => {
                              const updated = [...feedbackQuestions];
                              updated[idx] = { ...q, label: e.target.value };
                              setFeedbackQuestions(updated);
                            }}
                            placeholder="Question text"
                          />
                          <div className="flex items-center gap-3">
                            <select
                              className="input-base w-auto text-sm"
                              value={q.type}
                              onChange={(e) => {
                                const updated = [...feedbackQuestions];
                                updated[idx] = { ...q, type: e.target.value as 'text' | 'rating', maxRating: e.target.value === 'rating' ? (q.maxRating ?? 10) : undefined };
                                setFeedbackQuestions(updated);
                              }}
                            >
                              <option value="text">Free Text</option>
                              <option value="rating">Rating Scale</option>
                            </select>
                            {q.type === 'rating' && (
                              <div className="flex items-center gap-1 text-sm">
                                <span className="text-ink-secondary">Max:</span>
                                <input
                                  type="number"
                                  className="input-base w-16 text-center"
                                  value={q.maxRating ?? 10}
                                  onChange={(e) => {
                                    const updated = [...feedbackQuestions];
                                    updated[idx] = { ...q, maxRating: parseInt(e.target.value) || 10 };
                                    setFeedbackQuestions(updated);
                                  }}
                                  min={1}
                                  max={10}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-ghost text-red-500 p-1"
                          onClick={() => setFeedbackQuestions(feedbackQuestions.filter((_, i) => i !== idx))}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn-secondary mt-2 text-sm inline-flex items-center gap-1"
                  onClick={() => setFeedbackQuestions([...feedbackQuestions, { id: `q-${Date.now()}`, type: 'text', label: '' }])}
                >
                  <Plus className="h-3 w-3" /> Add Question
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Judge Assignment */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Judge Assignment
          </h2>
          <p className="mt-1 text-sm text-ink-secondary">
            Assign judges who can score submissions. Only assigned judges can submit scores.
          </p>

          {/* Search for users */}
          <div className="mt-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
              <input
                type="text"
                className="input-base pl-9"
                placeholder="Search users by name or email..."
                value={judgeSearch}
                onChange={(e) => setJudgeSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (judgeSearch.length < 2) return;
                    setJudgeSearching(true);
                    networkManager
                      .get<{ status: boolean; data: Array<{ sub: string; name: string; email: string }> }>(
                        `/api/users/search?q=${encodeURIComponent(judgeSearch)}&limit=10`,
                      )
                      .then((res) => setJudgeResults(res.data))
                      .catch(() => {})
                      .finally(() => setJudgeSearching(false));
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="btn-secondary"
              disabled={judgeSearching || judgeSearch.length < 2}
              onClick={() => {
                if (judgeSearch.length < 2) return;
                setJudgeSearching(true);
                networkManager
                  .get<{ status: boolean; data: Array<{ sub: string; name: string; email: string }> }>(
                    `/api/users/search?q=${encodeURIComponent(judgeSearch)}&limit=10`,
                  )
                  .then((res) => setJudgeResults(res.data))
                  .catch(() => {})
                  .finally(() => setJudgeSearching(false));
              }}
            >
              {judgeSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </button>
          </div>

          {/* Search results */}
          {judgeResults.length > 0 && (
            <div className="mt-2 card overflow-hidden">
              {judgeResults
                .filter((u) => !judges.includes(u.sub))
                .map((user) => (
                  <div key={user.sub} className="flex items-center justify-between border-b border-ring p-2 last:border-0">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-ink">
                        {user.name || user.email || user.sub}
                      </div>
                      <div className="truncate text-xs text-ink-tertiary">
                        {user.email || user.sub}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost text-xs text-green-700"
                      onClick={() => {
                        if (judges.length >= 20) return;
                        setJudges([...judges, user.sub]);
                        setJudgeDetails((prev) => ({
                          ...prev,
                          [user.sub]: {
                            sub: user.sub,
                            yumId: user.sub,
                            name: user.name,
                            email: user.email,
                            roles: [],
                          },
                        }));
                        setJudgeResults([]);
                        setJudgeSearch('');
                      }}
                    >
                      + Assign
                    </button>
                  </div>
                ))}
            </div>
          )}

          {/* Assigned judges */}
          {judges.length > 0 ? (
            <div className="mt-3 space-y-1">
              <p className="text-xs font-medium text-ink-secondary">
                {judges.length} judge{judges.length !== 1 ? 's' : ''} assigned (max 20)
              </p>
              {judges.map((yumId) => {
                const details = judgeDetails[yumId];
                const displayName = details?.name || yumId;
                const displayEmail = details?.email || `${yumId}@yum.com`;
                return (
                  <div
                    key={yumId}
                    className="flex items-center justify-between rounded-lg bg-surface-sunken p-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink">{displayName}</div>
                      <div className="flex items-center gap-1 truncate text-xs text-ink-tertiary">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{displayEmail}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost text-xs text-red-600"
                      onClick={() => setJudges(judges.filter((j) => j !== yumId))}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-3 text-sm text-ink-tertiary">
              No judges assigned. Anyone with the judge role can score while this list is empty.
            </p>
          )}
        </section>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/events/${eventId}`)}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || totalWeight !== 100}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
